// Home/Away 模式 → 裝置佈防計畫（APP#44，純函式、好測）。
//
// 「套用模式到裝置」＝用**既有** detection op:set 把警戒偵測開/關（不自創後端）：
//   - 外出（away / 佈防）：對可控且在線的攝影機開啟 Motion（0x8020）+ Object（0x8041）偵測
//     → 裝置產生位移/人形事件 → 推播佈防生效。
//   - 在家（home / 撤防）：關閉同一批偵測 → 在家不再被位移/人形告警打擾（放鬆）。
//     ⚠️ 只關「偵測告警觸發」，不動 RecordMode（連續錄影若已設定仍照錄，兩者正交）。
//
// 本檔只做**純規劃**（device list + mode → 每台要送的 op），side-effect（ws op:set / subscribe）
// 由 HomeAwayControl 執行，便於單測。arm 的 attr id 直接引用 device settings 的 SSOT
// （settings/ids.ts，本身逐字抄自 common/04 §4，不重造 id）。

import { canControlDevice, tileState, type DeviceSummary } from '../devices/deviceModel';
import { CL_MOTION, CL_OBJECT, MOTION_ENABLED, OBJECT_ENABLED } from '../devices/settings/ids';
import type { HomeAwayMode } from './homeAwayMode';

export interface ArmOp {
  id: string;
  value: boolean;
}

export interface ArmTarget {
  deviceId: string;
  name: string;
  ops: ArmOp[];
}

export interface ArmSkips {
  /** 有偵測能力但離線（op:set 會逾時，不送）。 */
  offline: number;
  /** 有偵測能力但無控制權（shared 角色，fail-closed 不送）。 */
  noPermission: number;
}

export interface ArmPlan {
  mode: HomeAwayMode;
  /** 目標開/關值：away→true（開偵測）、home→false（關偵測）。 */
  value: boolean;
  targets: ArmTarget[];
  skipped: ArmSkips;
}

function clustersOf(d: DeviceSummary): Set<string> {
  const set = new Set<string>();
  for (const c of d.capability_effective ?? []) {
    if (c.cluster) set.add(c.cluster.toLowerCase());
  }
  return set;
}

/**
 * 是否可控（21 §2.1：owner/admin 或 shared-user 具 `control` permission；缺 role
 * fail-closed，APP#61）。權威仍是 server fail-closed（ADR-009）；與 DeviceSettings.tsx 同 pattern。
 */
function canControl(d: DeviceSummary): boolean {
  return canControlDevice(d);
}

function displayName(d: DeviceSummary): string {
  return d.aliasname || d.display_name || d.model_name || d.device_id;
}

/**
 * 依模式產生佈防計畫。只有「具偵測能力（Motion 或 Object cluster）」的裝置參與：
 *   - 無偵測能力（純插座 / 感測 / 燈）→ 完全略過、不計數。
 *   - 有偵測能力但無控制權 → 計入 skipped.noPermission。
 *   - 有偵測能力、可控但離線 → 計入 skipped.offline。
 *   - 有偵測能力、可控、在線（含 stale）→ 成為 target，帶 Motion(+Object) op。
 */
export function planArm(devices: DeviceSummary[], mode: HomeAwayMode): ArmPlan {
  const value = mode === 'away';
  const targets: ArmTarget[] = [];
  const skipped: ArmSkips = { offline: 0, noPermission: 0 };

  for (const d of devices) {
    const clusters = clustersOf(d);
    const hasMotion = clusters.has(CL_MOTION);
    const hasObject = clusters.has(CL_OBJECT);
    if (!hasMotion && !hasObject) continue; // 非偵測裝置，不參與安全模式

    if (!canControl(d)) {
      skipped.noPermission += 1;
      continue;
    }
    if (tileState(d) === 'offline') {
      skipped.offline += 1;
      continue;
    }

    const ops: ArmOp[] = [];
    if (hasMotion) ops.push({ id: MOTION_ENABLED, value });
    if (hasObject) ops.push({ id: OBJECT_ENABLED, value });
    targets.push({ deviceId: d.device_id, name: displayName(d), ops });
  }

  return { mode, value, targets, skipped };
}

/** 便捷：計畫中會被實際套用的裝置台數。 */
export function armableCount(plan: ArmPlan): number {
  return plan.targets.length;
}
